import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, FileText, Settings, Menu, X, Award, TrendingUp, HelpCircle } from 'lucide-react';
import { useState, useEffect } from 'react';

export function Layout() {
  const [session, setSession] = useState<any>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Re-check session when location changes (e.g., after login navigation)
  useEffect(() => {
    checkSession();
  }, [location.pathname]);

  const checkSession = async () => {
    try {
      const res = await fetch('/api/candidate/session', {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSession(data);
      }
    } catch (error) {
      console.error('Session check failed:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/candidate/logout', {
        method: 'POST',
        credentials: 'include'
      });
      setSession(null);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-kf-gray-50">
      {/* Header with logo, title, and user info */}
      <header className="bg-white border-b border-kf-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-center h-16">
            {/* Logo - left */}
            <Link to="/" className="absolute left-4 sm:left-6 lg:left-8">
              <img
                src="/kompetanseforbundet-logo.png"
                alt="Kompetanseforbundet"
                className="h-10 w-auto"
                onError={(e) => {
                  // Fallback if logo fails to load
                  e.currentTarget.style.display = 'none';
                  const fallback = document.getElementById('logo-fallback');
                  if (fallback) fallback.classList.remove('hidden');
                }}
              />
              <div id="logo-fallback" className="hidden w-10 h-10 bg-kf-orange-600 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </Link>

            {/* Title - centered */}
            <Link to="/" className="text-center">
              <div className="text-2xl font-bold text-kf-gray-900">Kompetanseattest</div>
              <div className="text-xs text-kf-gray-500">Kandidatportal</div>
            </Link>

            {/* User info and logout - right (only when logged in) */}
            {session?.authenticated && (
              <div className="absolute right-4 sm:right-6 lg:right-8 hidden md:flex flex-col items-end">
                <div className="text-sm font-medium text-kf-gray-900">
                  {session.user?.name}
                </div>
                <button
                  onClick={handleLogout}
                  className="text-xs text-kf-gray-500 hover:text-kf-error-600 flex items-center gap-1 transition-colors"
                >
                  <LogOut className="w-3 h-3" />
                  Logg ut
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Navigation bar - only when logged in */}
      {session?.authenticated && (
        <nav className="bg-white shadow-sm border-b border-kf-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Desktop Navigation - centered */}
            <div className="hidden md:flex items-center justify-center space-x-8 h-12">
              <Link
                to="/dashboard"
                className="text-kf-gray-700 hover:text-kf-orange-600 font-medium transition-colors"
              >
                Mine Attester
              </Link>
              <Link
                to="/cv-builder"
                className="text-kf-gray-700 hover:text-kf-orange-600 font-medium transition-colors flex items-center gap-2"
              >
                <Award className="w-4 h-4" />
                CV-Builder
              </Link>
              <Link
                to="/learning-path"
                className="text-kf-gray-700 hover:text-kf-orange-600 font-medium transition-colors flex items-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                Læringssti
              </Link>
              <Link
                to="/settings"
                className="text-kf-gray-700 hover:text-kf-orange-600 font-medium transition-colors flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Innstillinger
              </Link>
              <Link
                to="/welcome"
                className="text-kf-gray-700 hover:text-kf-orange-600 font-medium transition-colors flex items-center gap-2"
              >
                <HelpCircle className="w-4 h-4" />
                Hjelp
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center justify-end h-12">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-kf-gray-100"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-kf-gray-200 bg-white">
              <div className="px-4 py-4 space-y-2">
                {/* User info at top */}
                <div className="px-4 py-2 border-b border-kf-gray-200 mb-2 pb-2">
                  <div className="text-sm font-medium text-kf-gray-900">
                    {session.user?.name}
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-xs text-kf-gray-500 hover:text-kf-error-600 flex items-center gap-1 mt-1"
                  >
                    <LogOut className="w-3 h-3" />
                    Logg ut
                  </button>
                </div>
                <Link
                  to="/dashboard"
                  className="block px-4 py-2 text-kf-gray-700 hover:bg-kf-gray-100 rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mine Attester
                </Link>
                <Link
                  to="/cv-builder"
                  className="block px-4 py-2 text-kf-gray-700 hover:bg-kf-gray-100 rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  CV-Builder
                </Link>
                <Link
                  to="/learning-path"
                  className="block px-4 py-2 text-kf-gray-700 hover:bg-kf-gray-100 rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Læringssti
                </Link>
                <Link
                  to="/settings"
                  className="block px-4 py-2 text-kf-gray-700 hover:bg-kf-gray-100 rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Innstillinger
                </Link>
                <Link
                  to="/welcome"
                  className="block px-4 py-2 text-kf-gray-700 hover:bg-kf-gray-100 rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Hjelp
                </Link>
              </div>
            </div>
          )}
        </nav>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-kf-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col items-center gap-4">
            <img
              src="/kompetanseforbundet-logo.png"
              alt="Kompetanseforbundet"
              className="h-12 w-auto"
            />
            <div className="text-center text-sm text-kf-gray-500">
              <p>© {new Date().getFullYear()} Kompetanseforbundet Norge</p>
              <p className="mt-2">
                <a href="https://www.kompetanseforbundet.no" className="hover:text-kf-orange-600" target="_blank" rel="noopener noreferrer">
                  www.kompetanseforbundet.no
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
